import { useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { typography, semantic, shadow, radius } from '@/shared/theme/tokens';
import { useThemeColors, useIsDark } from '@/shared/theme/useThemeColors';
import { useBreakpoint } from '@/shared/lib/useBreakpoint';
import { useOverviewConfig } from '@/features/deploymentSettings/hooks/useOverviewConfig';
import { useParameters, useParameterLive } from '@/features/deploymentSettings/hooks/useDeploymentSettings';
import { useAlertsLive } from '@/features/alerts/hooks/useAlerts';
import { useRecommendationsLive } from '@/features/recommendations/hooks/useRecommendations';
import { ParameterChartsModal } from '@/features/deploymentSettings/ui/ParameterChartsModal';
import { useSvgTemplates } from '@/features/overviewDiagram/hooks/useSvgTemplates';
import { SvgDiagram } from '@/features/overviewDiagram/ui/SvgDiagram';
import { SkeletonCard } from '@/shared/ui/SkeletonCard';
import { SearchInput } from '@/shared/ui/SearchInput';
import { AppSelect } from '@/shared/ui/AppSelect';
import { SEVERITY_META, ALERT_CATEGORY_META } from '@/features/alerts/alertMeta';
import { PARAM_STATUS_META, PARAM_STATUS_FALLBACK_OK, PARAM_STATUS_FALLBACK_ALERT } from '@/shared/ui/paramStatusMeta';

// Critical Parameters island sizing (AUT-199): always fit up to 2 rows of cards
// without scroll; beyond that the island body scrolls. Height = cap derived from
// the (no-optimal-range) card height, not the flex container.
const CRIT_CARD_H = 106;     // measured card height (name + value rows + padding), optimal range removed
const CRIT_GRID_GAP = 10;
const CRIT_BODY_PAD = 16;   // OverviewIsland body vertical padding (top + bottom)
const CRIT_HEADER_H = 42;   // OverviewIsland header bar (12px pad top/bottom + icon line + border)
const CRIT_MAX_ROWS = 2;
const CRIT_ISLAND_MAX_H =
  CRIT_HEADER_H + CRIT_BODY_PAD * 2 + CRIT_CARD_H * CRIT_MAX_ROWS + CRIT_GRID_GAP * (CRIT_MAX_ROWS - 1);

function isInOptimal(value, param) {
  const lo = param.optimumLow != null && param.optimumLow !== '' ? Number(param.optimumLow) : null;
  const hi = param.optimumHigh != null && param.optimumHigh !== '' ? Number(param.optimumHigh) : null;
  if (lo === null && hi === null) return true;
  if (lo !== null && hi !== null) return value >= lo && value <= hi;
  if (lo !== null) return value >= lo;
  return value <= hi;
}

function liveStateMeta(live, param) {
  if (!live || live.value == null) return null;
  if (live.state) return PARAM_STATUS_META[live.state] ?? PARAM_STATUS_META.just_off;
  return isInOptimal(live.value, param) ? PARAM_STATUS_FALLBACK_OK : PARAM_STATUS_FALLBACK_ALERT;
}

// ─── Island shell (matches settings page pattern) ────────────────────────────

function OverviewIsland({ label, icon, iconColor, badge, action, c, isDark, children, scrollBody = false, style, collapsible = false, defaultCollapsed = false, fillHeight = false }) {
  const [collapsed, setCollapsed] = useState(defaultCollapsed);
  return (
    <div style={{
      background: isDark ? 'rgba(255,255,255,0.025)' : '#fff',
      border: `1px solid ${c.border}`,
      borderRadius: radius['2xl'],
      boxShadow: shadow.panel,
      overflow: 'hidden',
      display: 'flex',
      flexDirection: 'column',
      flexShrink: 0,
      height: (!collapsed && fillHeight) ? '100%' : 'auto',
      ...style,
    }}>
      {/* Island header bar */}
      <div
        onClick={collapsible ? () => setCollapsed((v) => !v) : undefined}
        style={{
          display: 'flex', alignItems: 'center', gap: 8,
          padding: '12px 18px',
          borderBottom: collapsed ? 'none' : `1px solid ${c.border}`,
          background: isDark ? 'rgba(255,255,255,0.02)' : 'rgba(248,250,252,1)',
          flexShrink: 0,
          cursor: collapsible ? 'pointer' : 'default',
          userSelect: 'none',
        }}
      >
        <span className="material-symbols-outlined" style={{ fontSize: 16, color: iconColor ?? c.textSubtle }}>{icon}</span>
        <span style={{ fontSize: typography.fontSize.md, fontWeight: typography.fontWeight.bold, color: c.textPrimary, fontFamily: typography.fontFamily.body, letterSpacing: typography.letterSpacing.normal, flex: 1 }}>
          {label}
        </span>
        {badge}
        {action && (
          <div onClick={(e) => e.stopPropagation()}>
            {action}
          </div>
        )}
        {collapsible && (
          <span className="material-symbols-outlined" style={{ fontSize: 16, color: c.textSubtle, transition: 'transform 0.2s', transform: collapsed ? 'rotate(0deg)' : 'rotate(180deg)', marginLeft: 2, display: 'flex', alignItems: 'center', lineHeight: 1 }}>
            expand_more
          </span>
        )}
      </div>
      {/* Island body — hidden when collapsed */}
      {!collapsed && (
        <div style={{ padding: '16px 18px', flex: 1, minHeight: 0, overflowY: scrollBody ? 'auto' : 'visible', display: 'flex', flexDirection: 'column' }}>
          {children}
        </div>
      )}
    </div>
  );
}

// ─── Main Page ───────────────────────────────────────────────────────────────

export default function DeploymentOverviewPage() {
  const { id: depId } = useParams();
  const c = useThemeColors();
  const isDark = useIsDark();
  const navigate = useNavigate();
  const { isMobile, isTablet } = useBreakpoint();
  const isNarrow = isMobile || isTablet;

  const { data: config, isLoading: configLoading } = useOverviewConfig(depId);
  const { data: allParams = [], isLoading: paramsLoading } = useParameters(depId);
  const { data: liveValues = [] } = useParameterLive(depId);
  const { data: alerts = [] } = useAlertsLive(depId);
  const { data: recs = [] } = useRecommendationsLive(depId);

  const [chartParam, setChartParam] = useState(null);

  const liveMap = Object.fromEntries(liveValues.map((v) => [v.parameterId, v]));
  const isLoading = configLoading || paramsLoading;

  if (isLoading) return (
    <div style={{ display: 'flex', flexDirection: isNarrow ? 'column' : 'row', gap: 16, height: isNarrow ? 'auto' : '100%', fontFamily: typography.fontFamily.body }}>
      <div style={{ flex: 2, display: 'flex', flexDirection: 'column', gap: 16 }}>
        <SkeletonCard rows={2} height={220} />
        <SkeletonCard rows={4} height={isNarrow ? 260 : undefined} style={{ flex: 1 }} />
      </div>
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: 16 }}>
        <SkeletonCard rows={1} height={52} />
        <SkeletonCard rows={1} height={52} />
        <SkeletonCard rows={3} height={isNarrow ? 400 : undefined} style={{ flex: 1 }} />
      </div>
    </div>
  );

  const criticalParams = (config?.criticalParameterIds ?? [])
    .map((id) => allParams.find((p) => p.id === id))
    .filter(Boolean);

  const sections = config?.sections
    ? [...config.sections].sort((a, b) => (a.order ?? 0) - (b.order ?? 0))
    : [];

  // Live alerts endpoint returns only active alerts — no status filter needed
  const activeAlerts = alerts;
  // Live recs endpoint returns unacknowledged + accepted recs
  const activeRecs = recs.filter((r) => r.status === 'unacknowledged' || r.status === 'accepted');
  const criticalAlertCount = activeAlerts.filter((a) => a.severity === 'critical').length;

  if (criticalParams.length === 0 && sections.length === 0 && !config) {
    return (
      <div style={{ height: '100%' }}>
        <EmptyOverviewState depId={depId} navigate={navigate} c={c} isDark={isDark} />
      </div>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: isNarrow ? 'column' : 'row', gap: 16, height: isNarrow ? 'auto' : '100%', fontFamily: typography.fontFamily.body, minHeight: 0, overflowY: isNarrow ? 'auto' : 'hidden' }}>

      {/* LEFT COLUMN: Critical params + SVG — 2/3 width */}
      <div style={{ flex: isNarrow ? 'none' : 2, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 16, minHeight: 0 }}>

        {/* Critical Parameters — height fits up to 2 card rows, scrolls beyond (AUT-199) */}
        <div style={isNarrow ? { minHeight: 180 } : { flexShrink: 0, maxHeight: CRIT_ISLAND_MAX_H }}>
          <OverviewIsland
            label="Critical Parameters"
            icon="warning"
            iconColor={semantic.warning}
            badge={<CountBadge count={criticalParams.length} color={c.textSubtle} bg={c.bgSubtle} />}
            scrollBody
            c={c}
            isDark={isDark}
          >
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 10 }}>
              {criticalParams.map((param) => (
                <CriticalParamCard
                  key={param.id}
                  param={param}
                  live={liveMap[param.id]}
                  onClick={() => setChartParam(param)}
                  c={c}
                  isDark={isDark}
                />
              ))}
            </div>
          </OverviewIsland>
        </div>

        {/* HMI diagram — 2/3 height on desktop, fixed on mobile */}
        <div style={isNarrow ? { height: 320 } : { flex: 2, minHeight: 0 }}>
          <DiagramSection
            config={config}
            allParams={allParams}
            liveMap={liveMap}
            onParamClick={setChartParam}
            navigate={navigate}
            depId={depId}
            c={c}
            isDark={isDark}
          />
        </div>
      </div>

      {/* RIGHT COLUMN: Alerts + Recs + Parameter Sections — 1/3 width */}
      <div style={{ flex: 1, minWidth: 0, display: 'flex', flexDirection: 'column', gap: 16, minHeight: isNarrow ? 'auto' : 0 }}>

        {/* Alerts + Recs stacked — fixed height each */}
        <div style={{ display: 'flex', flexDirection: 'column', gap: 16, flexShrink: 0 }}>

          {/* Alerts island */}
          <OverviewIsland
            collapsible
            defaultCollapsed
            scrollBody
            style={{ maxHeight: 200 }}
            label="Active Alerts"
            icon="notifications_active"
            iconColor={criticalAlertCount > 0 ? semantic.error : c.textSubtle}
            badge={
              <CountBadge
                count={activeAlerts.length}
                color={criticalAlertCount > 0 ? semantic.error : c.textSubtle}
                bg={criticalAlertCount > 0 ? 'rgba(220,38,38,0.08)' : c.bgSubtle}
              />
            }
            action={
              <button onClick={() => navigate(`/deployments/${depId}/alerts`)} style={navLinkStyle(c)}>
                View all
                <span className="material-symbols-outlined" style={{ fontSize: 12 }}>arrow_forward</span>
              </button>
            }
            c={c}
            isDark={isDark}
          >
            {activeAlerts.length === 0 ? (
              <span style={{ fontSize: typography.fontSize.base, color: c.textMuted, fontStyle: 'italic' }}>No active alerts</span>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {activeAlerts.slice(0, 5).map((a) => {
                  const catMeta = ALERT_CATEGORY_META[a.category];
                  const sevMeta = SEVERITY_META[a.severity];
                  const color = catMeta?.color ?? sevMeta?.color ?? c.textSubtle;
                  const bg = catMeta?.bgLight ?? sevMeta?.bgLight ?? c.bgSubtle;
                  return (
                    <AlertRow key={a.id} color={color} bg={bg} label={catMeta?.label ?? sevMeta?.label} c={c}>
                      {a.parameterName ?? a.id}
                    </AlertRow>
                  );
                })}
              </div>
            )}
          </OverviewIsland>

          {/* Recs island */}
          <OverviewIsland
            collapsible
            defaultCollapsed
            scrollBody
            style={{ maxHeight: 200 }}
            label="Recommendations"
            icon="lightbulb"
            iconColor={activeRecs.length > 0 ? semantic.warning : c.textSubtle}
            badge={
              <CountBadge
                count={activeRecs.length}
                color={activeRecs.length > 0 ? semantic.warning : c.textSubtle}
                bg={activeRecs.length > 0 ? 'rgba(217,119,6,0.08)' : c.bgSubtle}
              />
            }
            action={
              <button onClick={() => navigate(`/deployments/${depId}/recommendations`)} style={navLinkStyle(c)}>
                View all
                <span className="material-symbols-outlined" style={{ fontSize: 12 }}>arrow_forward</span>
              </button>
            }
            c={c}
            isDark={isDark}
          >
            {activeRecs.length === 0 ? (
              <span style={{ fontSize: typography.fontSize.base, color: c.textMuted, fontStyle: 'italic' }}>No active recommendations</span>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
                {activeRecs.slice(0, 4).map((r) => (
                  <AlertRow key={r.id} color={semantic.warning} bg="rgba(217,119,6,0.08)" c={c}>
                    {r.parameterName ?? r.id}
                  </AlertRow>
                ))}
              </div>
            )}
          </OverviewIsland>
        </div>

        {/* Parameter Sections — takes all remaining height on desktop, fixed on mobile */}
        {sections.length > 0 && (
          <div style={isNarrow ? { height: 400 } : { flex: 1, minHeight: 0 }}>
            <ParameterSections
              sections={sections}
              allParams={allParams}
              liveMap={liveMap}
              onParamClick={setChartParam}
              c={c}
              isDark={isDark}
            />
          </div>
        )}
      </div>

      {chartParam && (
        <ParameterChartsModal
          depId={depId}
          param={chartParam}
          allParams={allParams}
          initialRangeDays={7}
          onClose={() => setChartParam(null)}
        />
      )}
    </div>
  );
}

// ─── Critical Param Card ─────────────────────────────────────────────────────

function CriticalParamCard({ param, live, onClick, c, isDark }) {
  const [hovered, setHovered] = useState(false);
  const meta = liveStateMeta(live, param);
  const valueColor = meta ? meta.color : c.textPrimary;
  const cardBg = meta ? meta.bg : (isDark ? 'rgba(255,255,255,0.04)' : c.bgSubtle);
  const cardBorder = meta ? meta.border : c.borderSubtle;

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        borderRadius: radius.lg,
        padding: '14px 16px',
        background: cardBg,
        border: `1.5px solid ${cardBorder}`,
        boxShadow: hovered ? shadow.card : 'none',
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        gap: 8,
        transition: 'box-shadow 0.15s, transform 0.1s',
        transform: hovered ? 'translateY(-1px)' : 'none',
      }}
    >
      <div style={{ display: 'flex', alignItems: 'flex-start', gap: 6 }}>
        <span style={{
          fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.semibold, color: c.textSubtle,
          flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
          letterSpacing: typography.letterSpacing.wider,
        }}>
          {param.displayName}
        </span>
        {meta && (
          <span style={{
            display: 'inline-flex', alignItems: 'center', gap: 3,
            padding: '2px 7px', borderRadius: radius.full,
            background: meta.bg, border: `1px solid ${meta.border}`,
            fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold, color: meta.color, flexShrink: 0,
          }}>
            <span className="material-symbols-outlined" style={{ fontSize: 10, fontVariationSettings: "'FILL' 1" }}>{meta.icon}</span>
            {meta.label}
          </span>
        )}
      </div>

      <div style={{ display: 'flex', alignItems: 'baseline', gap: 5 }}>
        <span style={{ fontSize: typography.fontSize['6xl'], fontWeight: typography.fontWeight.bold, color: valueColor, fontFamily: typography.fontFamily.heading, lineHeight: 1 }}>
          {live?.value != null ? Number(live.value).toFixed(2) : '—'}
        </span>
        {param.unit && (
          <span style={{ fontSize: typography.fontSize.base, color: c.textSubtle, fontWeight: typography.fontWeight.medium }}>{param.unit}</span>
        )}
      </div>
    </div>
  );
}

// ─── Alert row ───────────────────────────────────────────────────────────────

function AlertRow({ color, bg, label, c, children }) {
  return (
    <div style={{ display: 'flex', alignItems: 'center', gap: 7, minWidth: 0 }}>
      <span style={{ width: 6, height: 6, borderRadius: '50%', background: color, flexShrink: 0 }} />
      <span style={{ fontSize: typography.fontSize.base, color: c.textPrimary, flex: 1, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
        {children}
      </span>
      {label && (
        <span style={{
          fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold, color,
          background: bg, borderRadius: radius.xs, padding: '1px 6px', flexShrink: 0,
        }}>
          {label}
        </span>
      )}
    </div>
  );
}

// ─── Parameter Sections ───────────────────────────────────────────────────────

const ALL_SECTIONS_ID = '__all__';

function ParameterSections({ sections, allParams, liveMap, onParamClick, c, isDark }) {
  const [activeSection, setActiveSection] = useState(ALL_SECTIONS_ID);
  const [search, setSearch] = useState('');

  const visibleSections = (() => {
    const src = activeSection === ALL_SECTIONS_ID
      ? sections
      : sections.filter((s) => s.id === activeSection);
    return src.map((sec) => ({
      ...sec,
      params: sec.parameterIds
        .map((id) => allParams.find((p) => p.id === id))
        .filter(Boolean)
        .filter((p) => !search || p.displayName.toLowerCase().includes(search.toLowerCase())),
    })).filter((sec) => sec.params.length > 0);
  })();
  const totalParams = visibleSections.reduce((n, s) => n + s.params.length, 0);

  const sectionOptions = [
    { value: ALL_SECTIONS_ID, label: 'All sections' },
    ...sections.map((sec) => ({ value: sec.id, label: `${sec.name} (${sec.parameterIds.length})` })),
  ];

  return (
    <OverviewIsland
      label="Parameter Sections"
      icon="analytics"
      fillHeight
      c={c}
      isDark={isDark}
    >
      {/* Controls row — full island width so nothing overflows the narrow column */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12, minWidth: 0 }}>
        <div style={{ flex: '0 0 150px', minWidth: 0 }}>
          <AppSelect
            options={sectionOptions}
            value={activeSection}
            onChange={(v) => { setActiveSection(v ?? ALL_SECTIONS_ID); setSearch(''); }}
            size="sm"
            searchable={false}
            clearable={false}
            fullWidth
          />
        </div>
        <SearchInput
          value={search}
          onChange={setSearch}
          placeholder="Search…"
          size="sm"
          containerStyle={{ flex: '1 1 90px', minWidth: 70 }}
        />
      </div>

      {/* Card grid — scrolls independently */}
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }}>
        {totalParams === 0 ? (
          <div style={{ padding: '28px', textAlign: 'center', fontSize: typography.fontSize.md, color: c.textSubtle }}>
            {search ? 'No parameters match your search' : 'No parameters in this section'}
          </div>
        ) : (
          <div style={{ display: 'flex', flexDirection: 'column', gap: 16 }}>
            {visibleSections.map((sec) => (
              <div key={sec.id}>
                <div style={{
                  fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.semibold, letterSpacing: typography.letterSpacing.widest,
                  textTransform: 'uppercase', color: c.textSubtle,
                  marginBottom: 8, paddingBottom: 4,
                  borderBottom: `1px solid ${c.borderSubtle}`,
                }}>
                  {sec.name}
                  <span style={{ fontWeight: typography.fontWeight.normal, marginLeft: 6 }}>({sec.params.length})</span>
                </div>
                <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(160px, 1fr))', gap: 8 }}>
                  {sec.params.map((param) => (
                    <SectionParamCard
                      key={param.id}
                      param={param}
                      live={liveMap[param.id]}
                      onClick={() => onParamClick(param)}
                      c={c}
                      isDark={isDark}
                    />
                  ))}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </OverviewIsland>
  );
}

function SectionParamCard({ param, live, onClick, c, isDark }) {
  const [hovered, setHovered] = useState(false);
  const meta = liveStateMeta(live, param);
  const cardBg = meta ? meta.bg : (isDark ? 'rgba(255,255,255,0.04)' : c.bgSubtle);
  const cardBorder = meta ? meta.border : c.borderSubtle;

  return (
    <div
      onClick={onClick}
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      style={{
        borderRadius: radius.lg,
        padding: '11px 13px',
        background: cardBg,
        border: `1px solid ${cardBorder}`,
        cursor: 'pointer',
        display: 'flex',
        flexDirection: 'column',
        gap: 5,
        boxShadow: hovered ? shadow.panel : 'none',
        transition: 'box-shadow 0.12s, transform 0.1s',
        transform: hovered ? 'translateY(-1px)' : 'none',
      }}
    >
      <span style={{
        fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.semibold, color: c.textSubtle,
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        letterSpacing: typography.letterSpacing.wide,
      }}>
        {param.displayName}
      </span>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 4 }}>
        <span style={{
          fontSize: typography.fontSize['3xl'], fontWeight: typography.fontWeight.bold,
          color: meta ? meta.color : c.textPrimary,
          fontFamily: typography.fontFamily.heading, lineHeight: 1,
        }}>
          {live?.value != null ? Number(live.value).toFixed(2) : '—'}
        </span>
        {param.unit && (
          <span style={{ fontSize: typography.fontSize.xs, color: c.textSubtle }}>{param.unit}</span>
        )}
      </div>
      {meta && (
        <div style={{ display: 'flex', alignItems: 'center', gap: 3 }}>
          <span className="material-symbols-outlined" style={{ fontSize: 10, color: meta.color, fontVariationSettings: "'FILL' 1" }}>{meta.icon}</span>
          <span style={{ fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold, color: meta.color }}>{meta.label}</span>
        </div>
      )}
    </div>
  );
}

// ─── Shared helpers ───────────────────────────────────────────────────────────

function CountBadge({ count, color, bg }) {
  return (
    <span style={{
      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
      minWidth: 20, height: 18, borderRadius: radius.lg, padding: '0 5px',
      background: bg, color, fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.bold,
    }}>
      {count}
    </span>
  );
}

function navLinkStyle(c) {
  return {
    display: 'inline-flex', alignItems: 'center', gap: 3,
    padding: '3px 8px', borderRadius: 6,
    border: `1px solid ${c.borderSubtle}`,
    background: 'transparent',
    fontSize: 11, fontWeight: 600,
    color: semantic.brandNavyMid,
    cursor: 'pointer',
    fontFamily: typography.fontFamily.body,
    flexShrink: 0,
  };
}

// ─── Coming Soon ─────────────────────────────────────────────────────────────


function DiagramSection({ config, allParams, liveMap, onParamClick, navigate, depId, c, isDark }) {
  const { templates } = useSvgTemplates();
  const svgTemplateId = config?.svgTemplateId ?? null;
  const svgSections = config?.svgSections ?? [];
  const template = templates.find((t) => t.id === svgTemplateId) ?? null;

  const boundCount = svgSections.filter((s) => (s.parameterIds?.length ?? 0) > 0).length;

  return (
    <OverviewIsland
      label={template ? template.name : 'Plant Diagram'}
      icon="schema"
      badge={template && boundCount > 0 ? (
        <span style={{
          display: 'inline-flex', alignItems: 'center', gap: 4,
          padding: '3px 10px', borderRadius: radius.full,
          background: isDark ? 'rgba(62,181,231,0.12)' : 'rgba(62,181,231,0.08)',
          border: `1px solid ${semantic.brandBorderAccentMid}`,
          fontSize: typography.fontSize.xs, fontWeight: typography.fontWeight.bold, color: semantic.brandAccent, letterSpacing: typography.letterSpacing.wider,
        }}>
          <span className="material-symbols-outlined" style={{ fontSize: 11 }}>touch_app</span>
          {boundCount} CLICKABLE ZONES
        </span>
      ) : null}
      fillHeight
      c={c}
      isDark={isDark}
    >
      {!template ? (
        <div style={{
          width: '100%', flex: 1, minHeight: 0, borderRadius: radius.lg,
          background: isDark ? 'rgba(255,255,255,0.02)' : c.bgSubtle,
          border: `1px dashed ${c.border}`,
          display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 8,
        }}>
          <span className="material-symbols-outlined" style={{ fontSize: 40, color: c.textMuted }}>schema</span>
          <span style={{ fontSize: typography.fontSize.md, fontWeight: typography.fontWeight.semibold, color: c.textPrimary }}>No diagram configured</span>
          <span style={{ fontSize: typography.fontSize.base, color: c.textSubtle, marginBottom: 8 }}>Pick an HMI diagram in Settings and assign parameters.</span>
          <button
            onClick={() => navigate(`/deployments/${depId}/settings?section=shifts`)}
            style={{
              padding: '8px 18px', borderRadius: radius.md, background: semantic.brandNavyMid, color: '#fff',
              border: 'none', fontSize: typography.fontSize.md, fontWeight: typography.fontWeight.semibold, cursor: 'pointer',
              fontFamily: typography.fontFamily.body, boxShadow: shadow.btnSm,
            }}
          >
            Configure Diagram
          </button>
        </div>
      ) : (
        <div style={{ width: '100%', flex: 1, minHeight: 0 }}>
          <SvgDiagram
            template={template}
            svgSections={svgSections}
            allParams={allParams}
            liveMap={liveMap}
            mode="interactive"
            onParamClick={onParamClick}
          />
        </div>
      )}
    </OverviewIsland>
  );
}

// ─── Empty state ──────────────────────────────────────────────────────────────

function EmptyOverviewState({ depId, navigate, c, isDark }) {
  return (
    <div style={{
      height: '100%', display: 'flex', flexDirection: 'column',
      alignItems: 'center', justifyContent: 'center', textAlign: 'center',
      gap: 8,
      borderRadius: radius['3xl'],
      background: isDark ? 'rgba(255,255,255,0.02)' : c.bgSubtle,
      border: `1px dashed ${c.borderSubtle}`,
    }}>
      <span className="material-symbols-outlined" style={{ fontSize: 40, color: c.textMuted, marginBottom: 4 }}>
        dashboard_customize
      </span>
      <p style={{ fontSize: typography.fontSize.lg, fontWeight: typography.fontWeight.semibold, color: c.textPrimary, margin: 0 }}>
        Overview not configured yet
      </p>
      <p style={{ fontSize: typography.fontSize.md, color: c.textSubtle, margin: '0 0 12px' }}>
        Set up critical parameters and sections in Settings.
      </p>
      <button
        onClick={() => navigate(`/deployments/${depId}/settings?section=shifts`)}
        style={{
          padding: '8px 20px', borderRadius: radius.md,
          background: semantic.brandNavyMid, color: '#fff',
          border: 'none', fontSize: typography.fontSize.md, fontWeight: typography.fontWeight.semibold,
          cursor: 'pointer', fontFamily: typography.fontFamily.body,
          boxShadow: shadow.btnSm,
        }}
      >
        Go to Settings
      </button>
    </div>
  );
}
